import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import Layout from "../../layout/Layout";
import { useParams } from "react-router-dom";
import { getData } from "../../helpers/utils";

const ServiceDetail = () => {
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [relatedServices, setRelatedServices] = useState([]);
  const r = useParams();

  useEffect(() => {
    setLoading(true);
    loadData();
  }, [r.key]);

  const loadData = async () => {
    const data = await getData();
    const currentDetail = data.services.find(
      (service) => service.key === r.key
    );
    setDetail(currentDetail);
    
    // Récupérer les services associés
    const otherServices = data.services
      .filter(service => service.key !== r.key)
      .slice(0, 3);
    setRelatedServices(otherServices);
    
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  };

  if (loading || !detail) {
    return (
      <Layout pageName="Chargement...">
        <div className="d-flex align-items-center justify-content-center vh-100 fs-4 fw-semibold">
          Chargement en cours...
        </div>
      </Layout>
    );
  }

  return (
    <Layout pageName={detail.name}>
      {/* Hero Section avec fond sombre et dégradé */}
      <section className="bg-gradient-dark position-relative py-5">
        <div className="container py-4 mt-7">
          <div className="row align-items-center">
            <div className="col-lg-6 mb-4 mb-lg-0">
              <h1 className="display-4 text-white fw-bold">{detail?.name}</h1>
              <div className="border-start border-primary border-3 ps-3 mt-4">
                <p className="text-light fs-5">{detail.description}</p>
              </div>
            </div>
            <div className="col-lg-5 offset-lg-1">
              <div className="rounded-3 shadow-lg bg-white p-4">
                <h5 className="mb-3 text-primary">Besoin d'assistance ?</h5>
                <p className="mb-4">Notre équipe est prête à répondre à toutes vos questions.</p>
                <a href="#contact" className="btn btn-primary rounded-pill px-4">Contactez-nous</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section de contenu principal */}
      <section className="py-5">
        <div className="container">
          <div className="row">
            {/* Contenu principal*/}
            <div className="col-lg-9">
              <div className="service-content">
                {detail.detail?.map((section, idx) => {
                  if (section.type === "text") {
                    return (
                      <div key={idx} className="content-block text-block mb-5">
                        {section.title && (
                          <h2 className="h3 mb-3 pb-2 border-bottom border-light">{section.title}</h2>
                        )}
                        <div>
                          {section.paragraph.map((p, pIdx) => (
                            <div key={pIdx} className="mb-4">
                              {p.title && <h4 className="h5 text-primary mb-2 fw-bold">{p.title}</h4>}
                              <p className="mb-0 text-secondary">{p.text}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  } else if (section.type === "picture") {
                    // Grouper les images par paires
                    const pictures = section.pictures || [];
                    const picturePairs = [];
                    
                    for (let i = 0; i < pictures.length; i += 2) {
                      picturePairs.push(pictures.slice(i, i + 2));
                    }
                    
                    return picturePairs.map((pair, pairIdx) => (
                      <div key={`pair-${idx}-${pairIdx}`} className="content-block image-text-block mb-5">
                        <div className="row g-4">
                          {pair.map((pic, picIdx) => {
                            // Alterner layout: image gauche/texte droite puis texte gauche/image droite
                            const isImageLeft = (pairIdx % 2 === 0 && picIdx === 0) || (pairIdx % 2 === 1 && picIdx === 1);
                            
                            return (
                              <div key={`${idx}-${pairIdx}-${picIdx}`} className="col-12">
                                <div className="row align-items-center">
                                  <div className={`col-md-${isImageLeft ? '5' : '7'} ${isImageLeft ? 'order-md-1' : 'order-md-2'}`}>
                                    <a 
                                      href={pic.picture} 
                                      data-glightbox 
                                      data-gallery="service-gallery"
                                      className="image-card"
                                    >
                                      <img
                                        src={pic.picture}
                                        alt={`illustration-${picIdx}`}
                                        className="img-fluid rounded-3 shadow-sm"
                                        loading="lazy"
                                      />
                                    </a>
                                  </div>
                                  <div className={`col-md-${isImageLeft ? '7' : '5'} ${isImageLeft ? 'order-md-2' : 'order-md-1'} mt-3 mt-md-0`}>
                                    <div className="content-text p-3 p-md-4 bg-light rounded-3">
                                      <h4 className="h5 text-primary mb-3">Élément {pairIdx * 2 + picIdx + 1}</h4>
                                      <p className="mb-0">
                                        {pic.caption || "Description de cette image du service. Ce texte peut être personnalisé pour chaque image afin de fournir plus d'informations."}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ));
                  }
                  return null;
                })}
              </div>

              {/* Section d'avis */}
              <div className="bg-light rounded-3 p-4 mt-5">
                <div className="row align-items-center">
                  <div className="col-md-5">
                    <h5 className="mb-0">Ce service vous a-t-il été utile ?</h5>
                  </div>
                  <div className="col-md-4 text-center py-3 py-md-0">
                    <small className="text-muted">25 personnes sur 78 ont trouvé cela utile</small>
                  </div>
                  <div className="col-md-3 text-end">
                    <div className="btn-group" role="group">
                      <input type="radio" className="btn-check" name="btnradio" id="btnradio1"/>
                      <label className="btn btn-outline-primary" htmlFor="btnradio1">
                        <i className="fa-regular fa-thumbs-up me-2"></i>Oui
                      </label>
                      <input type="radio" className="btn-check" name="btnradio" id="btnradio2"/>
                      <label className="btn btn-outline-primary" htmlFor="btnradio2">
                        Non<i className="fa-regular fa-thumbs-down ms-2"></i>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Barre latérale */}
            <div className="col-lg-3 mt-5 mt-lg-0">
              <div className="sticky-top" style={{ top: "100px" }}>
                {/* Carte de contact  */}
                <div className="card border-0 shadow-sm rounded-3 mb-4">
                  <div className="card-body p-3">
                    <h6 className="card-title mb-3 text-primary">Services associés</h6>
                    <ul className="list-group list-group-flush">
                      {relatedServices.map((service, index) => (
                        <li key={service.key} className="list-group-item px-0 py-2 border-dashed">
                          <a 
                            href={`/services/${service.key}`} 
                            className="text-decoration-none small d-flex align-items-center"
                          >
                            <span className="badge bg-primary bg-opacity-10 text-primary me-1">
                              {String(index + 1).padStart(2, '0')}
                            </span>
                            <span className="text-truncate">{service.name}</span>
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                
                {/* Call to action - simplifié */}
                <div className="card bg-primary text-white border-0 rounded-3">
                  <div className="card-body p-3">
                    <h6 className="card-title text-white mb-2">Besoin d'aide ?</h6>
                    <p className="card-text small mb-3">Notre équipe est disponible pour vous accompagner.</p>
                    <a href="#contact" className="btn btn-sm btn-light text-primary fw-bold w-100">
                      Contactez-nous
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section de style pour les images de galerie */}
      <style jsx>{`
        .service-content {
          width: 100%;
        }
        
        .content-block {
          margin-bottom: 3rem;
        }
        
        .text-block {
          background-color: #f8f9fa;
          border-radius: 10px;
          padding: 25px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.05);
        }
        
        .image-text-block {
          margin-top: 40px;
          margin-bottom: 40px;
        }
        
        .image-card {
          display: block;
          position: relative;
          overflow: hidden;
          border-radius: 10px;
          box-shadow: 0 4px 15px rgba(0,0,0,0.1);
          transition: transform 0.3s ease;
        }
        
        .image-card:hover {
          transform: translateY(-5px);
        }
        
        .content-text {
          height: 100%;
          box-shadow: 0 2px 10px rgba(0,0,0,0.05);
        }
        
        .border-dashed {
          border-style: dashed;
          border-width: 0 0 1px 0;
          border-color: rgba(0,0,0,0.1);
        }
        
        .bg-gradient-dark {
          background: linear-gradient(135deg, #212529 0%, #343a40 100%);
        }
        
        @media (max-width: 767px) {
          .order-md-1, .order-md-2 {
            order: 0;
          }
          
          .content-text {
            margin-top: 15px;
          }
        }
      `}</style>
    </Layout>
  );
};

export default withTranslation()(ServiceDetail);